/**
 * Authentication utilities for consistent token handling.
 * Uses AWS Amplify v6 session format where accessToken.toString() returns JWT string.
 */
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Extracts the access token string from an Amplify session object.
 * Handles both live Amplify sessions (JwtPayload with .toString()) and
 * deserialized localStorage sessions (plain objects where .toString() returns "[object Object]").
 */
export const getAccessToken = (session: any): string => {
  const accessToken = session?.tokens?.accessToken;
  if (!accessToken) return '';

  if (typeof accessToken === 'string') return accessToken;

  const tokenStr = accessToken.toString();
  if (tokenStr && tokenStr !== '[object Object]') return tokenStr;

  // Fallback for deserialized sessions: check known token properties
  if (accessToken.jwtToken) return accessToken.jwtToken;

  return '';
};

/**
 * Fetches a fresh session and returns the access token string.
 * Use this in hooks/services that need the token for API calls.
 */
export const fetchAccessToken = async (): Promise<string> => {
  const session = await fetchAuthSession();
  const token = getAccessToken(session);
  if (!token) {
    throw new Error('No access token available');
  }
  return token;
};

/**
 * Creates an Authorization header with the Bearer token.
 */
export const getAuthHeader = (session: any) => {
  const token = getAccessToken(session);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Checks if a JWT token is expired.
 */
export const isTokenExpired = (token: string): boolean => {
  if (!token) return true;

  try {
    const payload = token.split('.')[1];
    if (!payload) return true;

    const decodedPayload = JSON.parse(atob(payload));
    if (!decodedPayload.exp) return false;

    const expirationTime = decodedPayload.exp * 1000;
    return Date.now() >= expirationTime;
  } catch {
    return true;
  }
};

/**
 * Refreshes the authentication session via Amplify.
 * Amplify stores the refreshed tokens in its configured cookie storage.
 */
export const refreshSession = async (): Promise<any> => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session;
  } catch (error) {
    console.error('Error refreshing session:', error);
    throw error;
  }
};
